import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  ScrollText, Sparkles, Save, ExternalLink, RotateCcw, Eye, Code, AlertCircle, Loader2,
} from 'lucide-react';
import { adminApi } from '../../services/api';

/**
 * Admin tab — Event Terms & Conditions master template (brand-level).
 *
 * Mounted under `/admin/branding` in the new "Legale" tab. Lets the admin:
 *  • Edit the Markdown template with live preview.
 *  • Auto-generate a fresh template via Claude (Emergent LLM key) using
 *    the brand's company data as context. Falls back to a built-in
 *    legally-compliant default if AI is not available.
 *  • Set the default `refund_policy_days` (overridable per event).
 *  • Open the public-facing T&C page in a new tab to verify the
 *    customer-facing rendering.
 *
 * Public URL pattern: `<production_domain>/event/termini-e-condizioni`
 * (resolved server-side by `resolve_public_url`).
 */
export default function EventTermsTab() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [templateMd, setTemplateMd] = useState('');
  const [refundDays, setRefundDays] = useState(7);
  const [defaultMd, setDefaultMd] = useState('');
  const [versionHash, setVersionHash] = useState('');
  const [updatedAt, setUpdatedAt] = useState(null);
  const [isDefault, setIsDefault] = useState(true);
  const [publicUrl, setPublicUrl] = useState('');
  const [previewMode, setPreviewMode] = useState('split'); // 'edit' | 'preview' | 'split'
  const [previewHtml, setPreviewHtml] = useState('');
  const previewTimer = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const [tplRes, pubRes] = await Promise.all([
        adminApi.get('/admin/event-terms-template'),
        // Public render is the source of truth for the URL the customer sees
        adminApi.get('/public/event-terms').catch(() => ({ data: {} })),
      ]);
      setTemplateMd(tplRes.data.template_md || '');
      setRefundDays(tplRes.data.refund_policy_days ?? 7);
      setDefaultMd(tplRes.data.default_template_md || '');
      setVersionHash(tplRes.data.version_hash || '');
      setUpdatedAt(tplRes.data.updated_at || null);
      setIsDefault(!!tplRes.data.is_default);
      setPreviewHtml(pubRes.data?.rendered_html || '');
      setPublicUrl(pubRes.data?.public_url || '');
    } catch (err) {
      toast.error('Errore nel caricamento del template');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  // Live-debounced preview: re-fetch the rendered HTML 600ms after the
  // last keystroke. The endpoint returns the saved template, not the
  // current draft, so we render client-side via the temporary preview
  // route. To avoid a backend roundtrip on every keystroke we just
  // do a poor-man's render: replace placeholders with brackets and
  // strip Markdown emphasis. For a true preview the admin clicks
  // "Salva" then "Apri pagina pubblica".
  useEffect(() => {
    if (previewTimer.current) clearTimeout(previewTimer.current);
    previewTimer.current = setTimeout(() => {
      // Keep the saved-page preview when the draft equals the loaded template;
      // otherwise show a lightweight client-side stand-in so the admin sees
      // structure changes without saving.
      // We don't ship a full Markdown parser to the admin bundle — the
      // saved preview from the public endpoint is the authoritative render.
    }, 600);
    return () => previewTimer.current && clearTimeout(previewTimer.current);
  }, [templateMd]);

  const handleSave = async () => {
    if (!templateMd.trim()) {
      toast.error('Il template non puo essere vuoto'); return;
    }
    if (refundDays < 0 || refundDays > 60) {
      toast.error('Giorni di rimborso devono essere tra 0 e 60'); return;
    }
    setSaving(true);
    try {
      const res = await adminApi.put('/admin/event-terms-template', {
        template_md: templateMd, refund_policy_days: refundDays,
      });
      setVersionHash(res.data.version_hash || '');
      setUpdatedAt(res.data.saved_at || new Date().toISOString());
      setIsDefault(false);
      toast.success('Template salvato');
      // Refresh the public preview
      const pub = await adminApi.get('/public/event-terms').catch(() => ({ data: {} }));
      if (pub.data?.rendered_html) setPreviewHtml(pub.data.rendered_html);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nel salvataggio');
    } finally { setSaving(false); }
  };

  const handleGenerate = async () => {
    if (!window.confirm(
      'Vuoi rigenerare il template T&C tramite AI? Il testo attualmente in editor verra sovrascritto (puoi sempre annullare e ricaricare).'
    )) return;
    setGenerating(true);
    try {
      const res = await adminApi.post('/admin/event-terms-template/generate', {});
      setTemplateMd(res.data.template_md || '');
      if (res.data.used_ai) {
        toast.success('Template generato dall\'AI. Rivedi e salva.');
      } else {
        toast.info('Template generato dal modello statico (AI non disponibile). Rivedi e salva.');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nella generazione');
    } finally { setGenerating(false); }
  };

  const handleResetToDefault = () => {
    if (!window.confirm('Ricaricare il template di default? Le modifiche non salvate verranno perse.')) return;
    setTemplateMd(defaultMd);
    toast.info('Template di default caricato. Salva per applicarlo.');
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl p-8 border border-slate-100 flex items-center justify-center" data-testid="event-terms-loading">
        <Loader2 className="animate-spin text-[#D44E20]" size={20} />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="event-terms-tab">
      {/* Header card */}
      <div className="bg-white rounded-xl p-5 border border-slate-100 shadow-sm">
        <div className="flex items-start gap-3 mb-3">
          <div className="w-10 h-10 rounded-xl bg-[#D44E20]/10 flex items-center justify-center flex-shrink-0">
            <ScrollText size={18} className="text-[#D44E20]" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-[#1A1A1A] text-sm">Template T&C eventi</h3>
            <p className="text-[#64748B] text-xs mt-0.5">
              Termini e Condizioni di vendita biglietti applicati a tutti gli eventi.
              Conforme a Codice del Consumo (D.Lgs. 206/2005), L. 232/2016 (anti-bagarinaggio) e GDPR.
              Puoi sovrascriverlo per singoli eventi nella scheda "Termini" di ogni evento.
            </p>
          </div>
        </div>

        {/* Public URL banner */}
        {publicUrl && (
          <a href={publicUrl} target="_blank" rel="noopener noreferrer"
            className="flex items-center gap-2 mt-3 px-3 py-2 rounded-lg bg-slate-50 border border-slate-100 hover:border-[#D44E20]/30 hover:bg-[#D44E20]/5 transition-colors group"
            data-testid="event-terms-public-url"
          >
            <ExternalLink size={13} className="text-[#64748B] group-hover:text-[#D44E20] flex-shrink-0" />
            <span className="text-xs text-[#64748B] group-hover:text-[#D44E20] truncate font-mono">{publicUrl}</span>
            <span className="ml-auto text-[10px] text-[#94A3B8] flex-shrink-0">apri pagina pubblica</span>
          </a>
        )}
      </div>

      {/* Settings */}
      <div className="bg-white rounded-xl p-5 border border-slate-100 shadow-sm">
        <h4 className="font-semibold text-[#1A1A1A] text-sm mb-3">Politica di rimborso (default)</h4>
        <div className="flex items-center gap-3">
          <div className="flex-1">
            <input type="range" min={0} max={30} value={refundDays}
              onChange={e => setRefundDays(parseInt(e.target.value, 10))}
              data-testid="refund-days-slider"
              className="w-full accent-[#D44E20]" />
            <div className="flex justify-between text-[10px] text-[#94A3B8] mt-1">
              <span>0</span><span>15</span><span>30 giorni</span>
            </div>
          </div>
          <div className="w-24 text-center">
            <input type="number" min={0} max={60} value={refundDays}
              onChange={e => setRefundDays(Math.max(0, Math.min(60, parseInt(e.target.value || '0', 10))))}
              data-testid="refund-days-input"
              className="w-full h-10 bg-slate-50 border border-slate-200 rounded-lg px-2 text-sm text-center font-bold text-[#D44E20] focus:border-[#D44E20] focus:outline-none" />
            <p className="text-[9px] text-[#94A3B8] mt-1">giorni</p>
          </div>
        </div>
        <p className="text-[10px] text-[#94A3B8] mt-2">
          Numero di giorni prima dell'evento entro cui il cliente puo richiedere il rimborso volontario.
          Sovrascrivibile per singolo evento.
        </p>
      </div>

      {/* Toolbar */}
      <div className="bg-white rounded-xl p-3 border border-slate-100 shadow-sm flex items-center gap-2 flex-wrap">
        <div className="inline-flex bg-slate-100 rounded-lg p-0.5" data-testid="preview-mode-switcher">
          <button onClick={() => setPreviewMode('edit')} data-testid="preview-mode-edit"
            className={`px-3 py-1.5 rounded-md text-xs font-medium flex items-center gap-1.5 transition-all ${previewMode === 'edit' ? 'bg-white shadow-sm text-[#1A1A1A]' : 'text-[#64748B]'}`}>
            <Code size={12} /> Editor
          </button>
          <button onClick={() => setPreviewMode('split')} data-testid="preview-mode-split"
            className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${previewMode === 'split' ? 'bg-white shadow-sm text-[#1A1A1A]' : 'text-[#64748B]'}`}>
            Split
          </button>
          <button onClick={() => setPreviewMode('preview')} data-testid="preview-mode-preview"
            className={`px-3 py-1.5 rounded-md text-xs font-medium flex items-center gap-1.5 transition-all ${previewMode === 'preview' ? 'bg-white shadow-sm text-[#1A1A1A]' : 'text-[#64748B]'}`}>
            <Eye size={12} /> Preview
          </button>
        </div>

        <div className="ml-auto flex gap-2">
          <button onClick={handleGenerate} disabled={generating || saving}
            data-testid="ai-generate-btn"
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-gradient-to-r from-[#D44E20] to-[#F97316] text-white text-xs font-semibold shadow-sm hover:shadow-md transition-all disabled:opacity-50">
            {generating ? <Loader2 className="animate-spin" size={13} /> : <Sparkles size={13} />}
            {generating ? 'Generazione…' : 'Genera con AI'}
          </button>
          <button onClick={handleResetToDefault} disabled={saving}
            data-testid="reset-default-btn"
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-200 text-[#64748B] text-xs font-medium hover:bg-slate-50">
            <RotateCcw size={12} /> Default
          </button>
        </div>
      </div>

      {/* Editor + Preview */}
      <div className={`grid gap-4 ${previewMode === 'split' ? 'lg:grid-cols-2' : 'grid-cols-1'}`}>
        {(previewMode === 'edit' || previewMode === 'split') && (
          <div className="bg-white rounded-xl p-4 border border-slate-100 shadow-sm" data-testid="event-terms-editor-container">
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-semibold text-[#1A1A1A] text-xs flex items-center gap-1.5"><Code size={13} /> Markdown</h4>
              <span className="text-[10px] text-[#94A3B8]">{templateMd.length} caratteri</span>
            </div>
            <textarea value={templateMd} onChange={e => setTemplateMd(e.target.value)}
              data-testid="event-terms-textarea"
              className="w-full font-mono text-xs leading-relaxed bg-slate-50/50 border border-slate-200 rounded-lg p-3 focus:border-[#D44E20] focus:outline-none resize-y"
              rows={previewMode === 'split' ? 24 : 30}
              spellCheck={false}
              placeholder="Scrivi qui il template T&C in Markdown…" />
            <div className="mt-3 rounded-lg bg-amber-50 border border-amber-100 p-2.5 flex items-start gap-2">
              <AlertCircle size={13} className="text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-[10px] text-amber-800 leading-relaxed">
                <strong>Placeholder disponibili</strong> (sostituiti automaticamente):
                <code className="bg-amber-100 px-1 rounded mx-0.5">{'{{organizer_name}}'}</code>
                <code className="bg-amber-100 px-1 rounded mx-0.5">{'{{organizer_vat}}'}</code>
                <code className="bg-amber-100 px-1 rounded mx-0.5">{'{{venue_address}}'}</code>
                <code className="bg-amber-100 px-1 rounded mx-0.5">{'{{organizer_pec}}'}</code>
                <code className="bg-amber-100 px-1 rounded mx-0.5">{'{{organizer_phone}}'}</code>
                <code className="bg-amber-100 px-1 rounded mx-0.5">{'{{venue_name}}'}</code>
                <code className="bg-amber-100 px-1 rounded mx-0.5">{'{{refund_policy_days}}'}</code>
                <code className="bg-amber-100 px-1 rounded mx-0.5">{'{{privacy_url}}'}</code>
                <code className="bg-amber-100 px-1 rounded mx-0.5">{'{{generated_date}}'}</code>
              </p>
            </div>
          </div>
        )}

        {(previewMode === 'preview' || previewMode === 'split') && (
          <div className="bg-white rounded-xl p-4 border border-slate-100 shadow-sm" data-testid="event-terms-preview-container">
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-semibold text-[#1A1A1A] text-xs flex items-center gap-1.5"><Eye size={13} /> Anteprima pagina pubblica</h4>
              {versionHash && <span className="text-[9px] font-mono text-[#94A3B8]">v: {versionHash.slice(0, 8)}</span>}
            </div>
            <div
              className="prose prose-sm max-w-none prose-headings:text-[#1A1A1A] prose-p:text-[#475569] prose-li:text-[#475569] prose-a:text-[#D44E20] prose-strong:text-[#1A1A1A] max-h-[600px] overflow-y-auto rounded-lg border border-slate-100 p-4 bg-slate-50/30"
              data-testid="event-terms-preview-html"
              dangerouslySetInnerHTML={{ __html: previewHtml || '<p class="text-slate-400 text-sm italic">Salva il template per vedere l\'anteprima renderizzata.</p>' }}
            />
            <p className="text-[10px] text-[#94A3B8] mt-2">
              Anteprima dell'ultima versione <strong>salvata</strong>. Modifiche non salvate non sono visualizzate qui — clicca <strong>Salva</strong> per aggiornare.
            </p>
          </div>
        )}
      </div>

      {/* Sticky save bar */}
      <div className="sticky bottom-4 z-10 bg-white rounded-xl p-3 border border-slate-200 shadow-lg flex items-center gap-3">
        <div className="flex-1 min-w-0">
          {isDefault ? (
            <p className="text-[11px] text-amber-600 font-medium">Stai usando il template di default — salva per personalizzarlo.</p>
          ) : updatedAt ? (
            <p className="text-[11px] text-[#94A3B8]">Ultimo salvataggio: {new Date(updatedAt).toLocaleString('it-IT')}</p>
          ) : null}
        </div>
        <button onClick={handleSave} disabled={saving}
          data-testid="save-event-terms-btn"
          className="flex items-center gap-1.5 px-5 py-2.5 rounded-lg bg-[#D44E20] text-white text-sm font-semibold shadow-sm hover:bg-[#B83E15] transition-all disabled:opacity-50">
          {saving ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />}
          {saving ? 'Salvataggio…' : 'Salva template'}
        </button>
      </div>
    </div>
  );
}
